import React, { useEffect, useState } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { API_ENDPOINTS } from '../config/api';

/**
 * Modal to generate HTML (or plain) content with Gemini.
 * mode: 'description' | 'blog' | 'application'
 */
const AiWriteModal = ({
  isOpen,
  onClose,
  onAccept,
  mode = 'description',
  postType = 'gig',
  defaultTitle = '',
  category = '',
  type = '',
  listingTitle = '',
  listingDescription = '',
  listingSkills = [],
  listingRequirements = [],
}) => {
  const [title, setTitle] = useState(defaultTitle);
  const [prompt, setPrompt] = useState('');
  const [preview, setPreview] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle(defaultTitle || listingTitle || '');
      setPrompt('');
      setPreview('');
      setExcerpt('');
      setHasGenerated(false);
      setLoading(false);
    }
  }, [isOpen, defaultTitle, listingTitle]);

  if (!isOpen) return null;

  const headings = {
    description: `Write ${postType === 'job' ? 'job' : 'gig'} description with AI`,
    blog: 'Write blog content with AI',
    application: 'Write application with AI',
  };

  const canGenerate =
    mode === 'application'
      ? true
      : title.trim().length > 0 && prompt.trim().length >= 10;

  const canAccept = hasGenerated && Boolean(preview.trim()) && !loading;

  const handleGenerate = async () => {
    if (mode !== 'application' && (!title.trim() || prompt.trim().length < 10)) {
      toast.error('Provide a title and a prompt (at least 10 characters)');
      return;
    }

    setLoading(true);
    setHasGenerated(false);
    try {
      if (mode === 'description') {
        const { data } = await api.post(API_ENDPOINTS.AI.GENERATE_DESCRIPTION, {
          postType,
          title: title.trim(),
          prompt: prompt.trim(),
          category,
          type,
        });
        const html = data.html || '';
        if (!html.trim()) {
          toast.error('AI returned empty content. Try again.');
          setPreview('');
          return;
        }
        setPreview(html);
        setHasGenerated(true);
      } else if (mode === 'blog') {
        const { data } = await api.post(API_ENDPOINTS.AI.GENERATE_BLOG, {
          title: title.trim(),
          prompt: prompt.trim(),
          category,
        });
        const html = data.html || '';
        if (!html.trim()) {
          toast.error('AI returned empty content. Try again.');
          setPreview('');
          setExcerpt('');
          return;
        }
        setPreview(html);
        setExcerpt(data.excerpt || '');
        setHasGenerated(true);
      } else {
        const { data } = await api.post(API_ENDPOINTS.AI.GENERATE_APPLICATION, {
          listingType: postType === 'gig' ? 'gig' : 'job',
          listingTitle: listingTitle || title,
          listingDescription,
          listingSkills,
          listingRequirements,
          prompt: prompt.trim(),
        });
        const text = data.text || '';
        if (!text.trim()) {
          toast.error('AI returned empty content. Try again.');
          setPreview('');
          return;
        }
        setPreview(text);
        setHasGenerated(true);
      }
    } catch (err) {
      setPreview('');
      setExcerpt('');
      setHasGenerated(false);
      toast.error(err.response?.data?.error || 'AI generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (!canAccept) {
      toast.error('Generate content first, then accept to fill the form');
      return;
    }
    onAccept({
      html: preview,
      text: preview,
      excerpt,
      title: title.trim(),
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent" />
            {headings[mode] || 'Write with AI'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {mode !== 'application' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full input-field"
                placeholder={mode === 'blog' ? 'Blog title' : `${postType === 'job' ? 'Job' : 'Gig'} title`}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {mode === 'application' ? 'Optional note for AI' : 'Prompt *'}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={mode === 'application' ? 3 : 4}
              className="w-full input-field"
              placeholder={
                mode === 'application'
                  ? 'e.g. Emphasize React and past marketplace work (optional)'
                  : mode === 'blog'
                    ? 'Describe the angle, audience, and key points you want covered...'
                    : 'Describe what this offer/role should cover, who it is for, scope, and outcomes...'
              }
            />
            {mode === 'application' && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                AI will write a short note (under 50 words) using this listing and your profile.
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={handleGenerate}
            disabled={loading || !canGenerate}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? 'Generating...' : hasGenerated ? 'Regenerate' : 'Generate'}
          </button>

          {loading && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Generating content…</p>
          )}

          {hasGenerated && preview && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Preview — review before filling the form
              </label>
              {mode === 'application' ? (
                <textarea
                  value={preview}
                  onChange={(e) => setPreview(e.target.value)}
                  rows={4}
                  className="w-full input-field text-sm"
                />
              ) : (
                <div
                  className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-800 dark:text-gray-200 prose dark:prose-invert max-w-none max-h-64 overflow-y-auto bg-gray-50 dark:bg-gray-900/40"
                  dangerouslySetInnerHTML={{ __html: preview }}
                />
              )}
              {excerpt && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Excerpt: {excerpt}
                </p>
              )}
              <p className="mt-2 text-xs text-accent">
                Click Accept &amp; Fill to insert this into the form. You can edit it there afterward.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
          {!canAccept && (
            <p className="text-xs text-gray-500 dark:text-gray-400 order-2 sm:order-1">
              Generate content first to enable Accept &amp; Fill.
            </p>
          )}
          <div className="flex justify-end gap-3 order-1 sm:order-2 sm:ml-auto">
            <button type="button" onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={!canAccept}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              title={!canAccept ? 'Generate content first' : 'Fill the form with this content'}
            >
              Accept &amp; Fill
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiWriteModal;
